import type {
  CanonicalTreeGeometryBranchRoot,
} from './CanonicalTreeGeometryBranchRootAuthority';

import {
  resolveCanonicalTreeFinalClusterLinePresentation,
  type CanonicalTreeFinalClusterLinePresentationResult,
  type CanonicalTreeFinalClusterLineTone,
} from './CanonicalTreeGeometryRootEnvelopeAuthority';

/*
 * CC-TREE-LIVE-003G2W
 *
 * Canonical Tree visual presentation authority.
 *
 * Presentation only.
 *
 * This authority consumes already-proven canonical geometry roots and
 * final-cluster line-presentation evidence. It cannot choose members,
 * generations, coordinates, bounds, routes, topology, parent ordering,
 * kinship, or viewer branch authority.
 */

export type CanonicalTreeVisualRootClass =
  | 'kc-tree-root-maternal'
  | 'kc-tree-root-paternal'
  | 'kc-tree-root-self'
  | 'kc-tree-root-descendant'
  | 'kc-tree-root-child-mediated'
  | 'kc-tree-root-both'
  | 'kc-tree-root-partner-affiliated'
  | 'kc-tree-root-affiliated'
  | 'kc-tree-root-unresolved';

const rootClassByCanonicalRoot:
  Readonly<
    Record<
      CanonicalTreeGeometryBranchRoot,
      CanonicalTreeVisualRootClass
    >
  > =
  Object.freeze({
    maternal:
      'kc-tree-root-maternal',

    paternal:
      'kc-tree-root-paternal',

    self:
      'kc-tree-root-self',

    descendant:
      'kc-tree-root-descendant',

    'child-mediated':
      'kc-tree-root-child-mediated',

    both:
      'kc-tree-root-both',

    'partner-affiliated':
      'kc-tree-root-partner-affiliated',

    affiliated:
      'kc-tree-root-affiliated',

    unresolved:
      'kc-tree-root-unresolved',
  });

export const resolveCanonicalTreeVisualRootClass = (
  root:
    CanonicalTreeGeometryBranchRoot,
): CanonicalTreeVisualRootClass =>
  rootClassByCanonicalRoot[root];

/*
 * CC-TREE-LIVE-003G3Q
 *
 * Cluster variation is PRESENTATION ONLY.
 *
 * The input must be canonical structural layout grammar. Opaque keys,
 * member identifiers, names, labels, coordinates, and CSS never acquire
 * palette authority.
 */
export type CanonicalTreeVisualClusterClass =
  | 'kc-tree-cluster-v0'
  | 'kc-tree-cluster-v1'
  | 'kc-tree-cluster-v2'
  | 'kc-tree-cluster-v3'
  | 'kc-tree-cluster-v4'
  | 'kc-tree-cluster-v5';

const canonicalTreeVisualClusterClasses:
  readonly CanonicalTreeVisualClusterClass[] =
  Object.freeze([
    'kc-tree-cluster-v0',
    'kc-tree-cluster-v1',
    'kc-tree-cluster-v2',
    'kc-tree-cluster-v3',
    'kc-tree-cluster-v4',
    'kc-tree-cluster-v5',
  ]);

const canonicalStructuralClusterKeyPattern =
  /^ancestor(?:\.\d+)+(?:\.child\.\d+)*(?:\.partner)?$/;

/*
 * Resolve the shared family branch used for visual differentiation.
 *
 * Deep ancestors and descendants underneath the same grandparent branch
 * intentionally collapse to one key:
 *
 *   ancestor.0.0
 *   ancestor.0.0.1
 *   ancestor.0.0.child.2
 *
 * all resolve to ancestor.0.0.
 */
export const resolveCanonicalTreeVisualClusterKey = (
  root:
    CanonicalTreeGeometryBranchRoot,
  clusterKey:
    string | null | undefined,
): string | null => {
  if (
    root !== 'maternal' &&
    root !== 'paternal'
  ) {
    return null;
  }

  const normalized =
    (clusterKey ?? '').trim();

  if (
    normalized.length === 0 ||
    !canonicalStructuralClusterKeyPattern.test(
      normalized,
    )
  ) {
    return null;
  }

  const tokens =
    normalized.split('.');

  const numericTokens: string[] =
    [];

  for (
    const token of
    tokens.slice(1)
  ) {
    if (
      token === 'child' ||
      token === 'partner'
    ) {
      break;
    }

    if (!/^\d+$/.test(token)) {
      return null;
    }

    numericTokens.push(token);

    if (
      numericTokens.length ===
      2
    ) {
      break;
    }
  }

  if (
    numericTokens.length === 0
  ) {
    return null;
  }

  return [
    'ancestor',
    ...numericTokens,
  ].join('.');
};

const canonicalTreeFamilyClusterClasses:
  readonly CanonicalTreeVisualClusterClass[] =
  Object.freeze([
    'kc-tree-cluster-v1',
    'kc-tree-cluster-v2',
    'kc-tree-cluster-v3',
    'kc-tree-cluster-v4',
    'kc-tree-cluster-v5',
  ]);

export const resolveCanonicalTreeVisualClusterClass = (
  root:
    CanonicalTreeGeometryBranchRoot,
  clusterKey:
    string | null | undefined,
): CanonicalTreeVisualClusterClass | null => {
  const familyClusterKey =
    resolveCanonicalTreeVisualClusterKey(
      root,
      clusterKey,
    );

  if (!familyClusterKey) {
    return null;
  }

  const numericTokens =
    familyClusterKey
      .split('.')
      .filter(
        token =>
          /^\d+$/.test(token),
      )
      .map(Number);

  /*
   * CC-TREE-LIVE-003G3R compatibility boundary.
   *
   * A one-level ancestry-root path is not yet a grandparent-family
   * cluster. Reserve v0 for that root-level structural path so the
   * historical 003G3Q root/child/child presentation distinction remains
   * intact while actual family branches continue to use v1-v5.
   */
  if (numericTokens.length === 1) {
    return 'kc-tree-cluster-v0';
  }

  const weightedPath =
    numericTokens.reduce(
      (sum, value, index) =>
        sum +
        (value + 1) *
          (index + 1),
      numericTokens.length,
    );

  return (
    canonicalTreeFamilyClusterClasses[
      weightedPath %
        canonicalTreeFamilyClusterClasses.length
    ] ?? null
  );
};

export type CanonicalTreeConnectorVisualClass =
  | 'kc-tree-line-maternal'
  | 'kc-tree-line-paternal'
  | 'kc-tree-line-viewer'
  | 'kc-tree-line-neutral';

export interface CanonicalTreeConnectorVisualPresentation {
  readonly className:
    CanonicalTreeConnectorVisualClass;

  readonly tone:
    CanonicalTreeFinalClusterLineTone;

  readonly disposition:
    CanonicalTreeFinalClusterLinePresentationResult[
      'disposition'
    ];

  readonly mutatesCanonicalGeometry:
    false;
}

const connectorClassByTone:
  Readonly<
    Record<
      CanonicalTreeFinalClusterLineTone,
      CanonicalTreeConnectorVisualClass
    >
  > =
  Object.freeze({
    'maternal-pink':
      'kc-tree-line-maternal',

    'paternal-blue':
      'kc-tree-line-paternal',

    'viewer-purple':
      'kc-tree-line-viewer',

    neutral:
      'kc-tree-line-neutral',
  });

export const resolveCanonicalTreeConnectorVisualPresentation = (
  sourceRoots:
    readonly CanonicalTreeGeometryBranchRoot[],
): CanonicalTreeConnectorVisualPresentation => {
  const authority =
    resolveCanonicalTreeFinalClusterLinePresentation({
      sourceRoots,
    });

  return Object.freeze({
    className:
      connectorClassByTone[
        authority.tone
      ],

    tone:
      authority.tone,

    disposition:
      authority.disposition,

    mutatesCanonicalGeometry:
      false,
  });
};

export const CANONICAL_TREE_CARD_VISUAL_CLASS =
  'kc-tree-card' as const;

export const CANONICAL_TREE_CENTERED_CARD_VISUAL_CLASS =
  'kc-tree-card-centered' as const;

export const CANONICAL_TREE_AVATAR_VISUAL_CLASS =
  'kc-tree-card-avatar' as const;

export const CANONICAL_TREE_AVATAR_FALLBACK_VISUAL_CLASS =
  'kc-tree-card-avatar-fallback' as const;

export const CANONICAL_TREE_CARD_ACCENT_VISUAL_CLASS =
  'kc-tree-card-accent' as const;

export const CANONICAL_TREE_ADD_BUTTON_VISUAL_CLASS =
  'kc-tree-add-button' as const;

export const CANONICAL_TREE_VIEWER_FRAME_VISUAL_CLASS =
  'kc-tree-viewer-frame' as const;

export const CANONICAL_TREE_FAMILY_UNIT_VISUAL_CLASS =
  'kc-tree-family-unit' as const;

export const CANONICAL_TREE_FAMILY_UNIT_HEART_VISUAL_CLASS =
  'kc-tree-family-unit-heart' as const;

export const CANONICAL_TREE_FAMILY_UNIT_DESCRIPTOR_VISUAL_CLASS =
  'kc-tree-family-unit-descriptor' as const;

export const CANONICAL_TREE_PENDING_VISUAL_CLASS =
  'kc-tree-status-pending' as const;

export const CANONICAL_TREE_SPOUSE_PARTNER_VISUAL_CLASS =
  'kc-tree-spouse-partner' as const;

export const CANONICAL_TREE_SPOUSE_HEART_VISUAL_CLASS =
  'kc-tree-spouse-heart' as const;

export const CANONICAL_TREE_SPOUSE_HEART_ICON_VISUAL_CLASS =
  'kc-tree-spouse-heart-icon' as const;
